/* eslint-disable max-len */
import { NullFunction } from '@deqo/foundation';
import React, { useCallback, useState } from 'react';
import Loader from 'react-loader-spinner';
import { LFC } from '../../Legacy';
import { useSubmitter } from '../../platform/Hook';
import { ResultInfoForSubmitter } from '../../ui/ResultInfo';
import { Copy, Flexer, Spacer } from '../dashboard/elements/LayoutElements';
import { Button, Input, Prompt, remInPx } from './../../ui';
import { Popup, PopupProps } from './Popup';

export interface InputPopupArgs<T> {
    title?: string;
    placeholder?: string;
    copy?: string;
    current?: T;
    parse?(value: string): T;
    validate?(value: T): string | true;
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const inputPopup = <T extends unknown>(args: InputPopupArgs<T>) => ({ ui: InputPopup, args });

export const InputPopup: LFC<PopupProps<InputPopupArgs<any>>> = ({ action, dismiss, args }) => {

    const [value, setValue] = useState((args.current || '').toString());
    const [error, setError] = useState('');

    const onAction = useCallback((confirm: boolean) => {
        if (confirm) {
            const parsed = !!args.parse ? args.parse(value) : value;
            const result = ((args.validate || NullFunction)(parsed) ?? true) as any;
            if (result === true) {
                action(parsed);
            }
            else {
                setError(result);
            }
        }
        else {
            dismiss();
        }
    }, [args, value, action, dismiss]);

    return <Prompt onAction={onAction}>
        <Popup w={36}>
            <Popup.Close onAction={onAction} />
            <Popup.Title>{args.title || 'Input Value'}</Popup.Title>
            <Popup.Content>
                <Copy>{args.copy}</Copy>
                <Input autoFocus type="text" value={value} onChange={setValue} error={!!error} message={error} placeholder={args.placeholder} />
            </Popup.Content>
            <Popup.Actions>
                <Button variant="primary" onAction={() => onAction(true)}>
                    <Flexer flex="row" inline center minWidth="3" >OK</Flexer>
                </Button>
                <Button variant="common" onAction={() => onAction(false)}>
                    <Flexer flex="row" inline center minWidth="3" >Cancel</Flexer>
                </Button>
            </Popup.Actions>
        </Popup>
    </Prompt>;
};

export interface InputSubmitPopupArgs<T> extends InputPopupArgs<T> {
    submit(value: T): Promise<void>;
}

export const inputSubmitPopup = <T extends unknown>(args: InputSubmitPopupArgs<T>) => ({ ui: InputSubmitPopup, args });

export const InputSubmitPopup: LFC<PopupProps<InputSubmitPopupArgs<any>>> = ({ action, dismiss, args }) => {

    const [value, setValue] = useState((args.current || '').toString());
    const [error, setError] = useState('');

    const [submit, working, result] = useSubmitter(
        async (parsedValue: any) => await args.submit(parsedValue),
        [args]
    );

    const onAction = useCallback(async (confirm: boolean) => {
        if (working) return;
        if (confirm) {
            const parsed = !!args.parse ? args.parse(value) : value;
            const validOrError = ((args.validate || NullFunction)(parsed) ?? true) as any;
            if (validOrError === true) {
                const x = await submit(parsed);
                action(parsed);
            }
            else {
                setError(validOrError);
            }
        }
        else {
            dismiss();
        }
    }, [args, value, submit, working, action, dismiss]);

    return <Prompt onAction={onAction}>
        <Popup w={36}>
            <Popup.Close onAction={onAction} />
            <Popup.Title>{args.title || 'Input Value'}</Popup.Title>
            <Popup.Content>
                <Copy>{args.copy}</Copy>
                <Input autoFocus type="text" value={value} onChange={setValue} error={!!error} message={error} placeholder={args.placeholder} />
                <Spacer />
                <ResultInfoForSubmitter result={result} />
            </Popup.Content>
            <Popup.Actions>
                <Button disabled={working} variant="primary" onAction={() => onAction(true)}>
                    <Flexer flex="row" inline center minWidth="3" >
                        OK
                        &nbsp;
                        {working && <>
                            &nbsp;
                            <Loader type="TailSpin" color="#fff" height={remInPx(1.2)} width={remInPx(1.2)} />
                        </>
                        }
                    </Flexer>
                </Button>
                <Button disabled={working} variant="common" onAction={() => onAction(false)}>
                    <Flexer flex="row" inline center minWidth="3" >Cancel</Flexer>
                </Button>
            </Popup.Actions>
        </Popup>
    </Prompt>;
};
